import React, { useMemo, useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Eye } from "lucide-react";
import { Button } from "react-bootstrap";
import { saveAs } from "file-saver";
import { RiShareForwardLine, RiDownloadFill } from "react-icons/ri";
import { MdOutlineBlock, MdOutlineDeleteOutline } from "react-icons/md";
import { HiOutlineMail } from "react-icons/hi";
import { FaArrowLeft } from "react-icons/fa6";
import { SiTicktick } from "react-icons/si";
import { ImConfused } from "react-icons/im";
import { Document, Packer, Paragraph, TextRun } from "docx";
import "../../src/components/Student/StuStyles/StuLiveRegister.css";
import ApiService from "../nhrc_apis/ApiService";

const { RegistrationAPI } = ApiService;

function GnTotalApplicationsRes() {
  const [showPopup, setShowPopup] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const { jobId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [nameFilter, setNameFilter] = useState("");
  const [skillFilter, setSkillFilter] = useState("");
  const [emailFilter, setEmailFilter] = useState("");
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState(null);
  const pageSize = 10;

  const [jobTitle, setJobTitle] = useState("Unknown Position");

  useEffect(() => {
    if (location.state && location.state.jobTitle) {
      setJobTitle(location.state.jobTitle);
    }
  }, [location.state]);

  /* ================= API FETCH ================= */
  useEffect(() => {
    if (!jobId) return;
    fetchRegistrations();
  }, [jobId]);

  const fetchRegistrations = async () => {
    try {
      setLoading(true);
      const res = await RegistrationAPI.getEventRegistrations(jobId);
      const apiData =
        res?.data?.data ||
        res?.data?.registrations ||
        res?.data?.result ||
        res?.data ||
        [];
      setRegistrations(Array.isArray(apiData) ? apiData : []);
    } catch (err) {
      console.error(err);
      setRegistrations([]);
    } finally {
      setLoading(false);
    }
  };

  /* ================= MEMBER TYPE COUNT ================= */
  const memberStats = useMemo(() => {
    return registrations.reduce(
      (acc, r) => {
        const type = (r.member_type || "UNKNOWN").toUpperCase();
        if (type === "STUDENT") acc.student++;
        else if (type === "HR") acc.hr++;
        else acc.other++;
        return acc;
      },
      { student: 0, hr: 0, other: 0 }
    );
  }, [registrations]);

  /* ================= FILTER ================= */
  const filteredAll = useMemo(
    () =>
      registrations.filter((c) => {
        if (statusFilter !== "all" && (c.status || "").toLowerCase() !== statusFilter)
          return false;
        if (
          nameFilter &&
          !(c.name || c.full_name || "")
            .toLowerCase()
            .includes(nameFilter.toLowerCase())
        )
          return false;
        if (
          skillFilter &&
          !(c.nhrcid || c.nhrc_id || "")
            .toLowerCase()
            .includes(skillFilter.toLowerCase())
        )
          return false;
        if (
          emailFilter &&
          !(c.email || "").toLowerCase().includes(emailFilter.toLowerCase())
        )
          return false;
        return true;
      }),
    [registrations, statusFilter, nameFilter, skillFilter, emailFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredAll.length / pageSize));

  const paged = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredAll.slice(start, start + pageSize);
  }, [filteredAll, page]);

  const allSelected = paged.length > 0 && selectedIds.length === paged.length;

  const toggleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(paged.map((c) => c.id));
    else setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const getStatusBadgeClass = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "approved") return "border border-primary text-success bg-transparent";
    if (s === "rejected") return "border border-danger text-danger bg-transparent";
    return "border border-secondary text-secondary bg-transparent";
  };

  const resetFilters = () => {
    setStatusFilter("all");
    setNameFilter("");
    setSkillFilter("");
    setEmailFilter("");
    setPage(1);
  };

  const handleViewCandidate = (id) => {
    const candidate = registrations.find((c) => c.id === id);
    if (!candidate) return;
    setSelectedCandidate(candidate);
    setShowPopup(true);
  };

  const handleBackToJobs = () => {
    navigate(-1);
  };

  /* ================= APPROVE ================= */
  const handleApprove = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one candidate to approve.");
      return;
    }

    if (!window.confirm(`Approve ${selectedIds.length} candidate(s)?`)) return;

    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => RegistrationAPI.approveRegistration(id)));

      // update status locally
      setRegistrations((prev) =>
        prev.map((c) =>
          selectedIds.includes(c.id) ? { ...c, status: "Approved" } : c
        )
      );
      setSelectedIds([]);
      alert("Selected candidate(s) approved successfully.");
    } catch (err) {
      console.error(err);
      alert("Failed to approve some candidate(s). Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  /* ================= REJECT ================= */
  const handleReject = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one candidate to reject.");
      return;
    }

    if (!window.confirm(`Reject ${selectedIds.length} candidate(s)?`)) return;

    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => RegistrationAPI.rejectRegistration(id)));

      // update status locally
      setRegistrations((prev) =>
        prev.map((c) =>
          selectedIds.includes(c.id) ? { ...c, status: "Rejected" } : c
        )
      );
      setSelectedIds([]);
      alert("Selected candidate(s) rejected successfully.");
    } catch (err) {
      console.error(err);
      alert("Failed to reject some candidate(s). Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  /* ================= DELETE ================= */
  const handleDelete = async () => {
    if (selectedIds.length === 0) {
      alert("Please select candidate(s) to delete");
      return;
    }

    if (!window.confirm(`Delete ${selectedIds.length} candidate(s)?`)) return;

    try {
      setActionLoading(true);
      await Promise.all(selectedIds.map((id) => RegistrationAPI.deleteRegistration(id)));

      // remove deleted records locally
      setRegistrations((prev) =>
        prev.filter((c) => !selectedIds.includes(c.id))
      );
      setSelectedIds([]);
      setPage(1);
      alert("Selected candidate(s) deleted successfully.");
    } catch (err) {
      console.error(err);
      alert("Failed to delete some candidate(s). Please try again.");
    } finally {
      setActionLoading(false);
    }
  };

  /* ================= DOWNLOAD ================= */
  const handleDownloadSelected = async () => {
    if (selectedIds.length === 0) {
      alert("Please select at least one candidate to download.");
      return;
    }

    const selectedCandidates = registrations.filter((c) =>
      selectedIds.includes(c.id)
    );

    if (selectedCandidates.length === 0) {
      alert("No matching candidates found.");
      return;
    }

    const doc = new Document({
      sections: selectedCandidates.map((c) => ({
        properties: {},
        children: [
          new Paragraph({
            children: [
              new TextRun({
                text: "Candidate Details",
                bold: true,
                size: 28,
              }),
            ],
          }),
          new Paragraph(""),
          new Paragraph(`ID: ${c.id}`),
          new Paragraph(`Name: ${c.name || c.full_name}`),
          new Paragraph(`Email: ${c.email}`),
          new Paragraph(`Phone: ${c.phone}`),
          new Paragraph(`NHRC ID: ${c.nhrcid || c.nhrc_id}`),
          new Paragraph(`Status: ${c.status}`),
          new Paragraph(""),
          new Paragraph(
            "------------------------------------------------------"
          ),
          new Paragraph(""),
        ],
      })),
    });

    try {
      const blob = await Packer.toBlob(doc);
      const fileName = `candidates_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.docx`;
      saveAs(blob, fileName);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download candidates.");
    }
  };

  return (
    <div className="container-fluid py-3 mt-5">
      <h3 className="training-title mt-2 fw-bold" style={{ color: "#8D0000" }}>GLOBAL NETWORKING</h3>

      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="justify-content-start">
          <button
            className="btn btn-outline-secondary btn-sm mb-2"
            onClick={handleBackToJobs}
          >
            <FaArrowLeft size={18} /> Back to Jobs
          </button>
          <h5 className="mb-0">Student Responses</h5>
          <small className="text-muted">
            Position: <span className="fw-semibold">{jobTitle}</span>
          </small>
        </div>
        <div
          md={6}
          className="d-flex justify-content-md-end justify-content-start mt-2 mt-md-0"
        >
          <div className="btn-toolbar" role="toolbar">
            <div className="btn-group btn-group-sm" role="group">
              <Button
                variant="success"
                size="sm"
                className="me-2 border rounded-3"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                <SiTicktick /> Approve
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                className="me-2 border rounded-3"
                onClick={handleReject}
                disabled={actionLoading}
              >
                <ImConfused /> Reject
              </Button>
              <Button variant="primary" size="sm" className="me-2 border rounded-3">
                <HiOutlineMail /> Email
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2 border rounded-3"
                onClick={handleDownloadSelected}
                disabled={actionLoading}
              >
                <RiDownloadFill /> Download
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                className="border rounded-3"
                onClick={handleDelete}
                disabled={actionLoading}
              >
                <MdOutlineDeleteOutline /> Delete
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* MEMBER TYPE COUNT */}
      <div className="d-flex gap-3 my-2">
        <span className="badge bg-primary">STUDENT: {memberStats.student}</span>
        <span className="badge bg-success">HR: {memberStats.hr}</span>
        <span className="badge bg-secondary">OTHER: {memberStats.other}</span>
      </div>

      <div className="bg-light border rounded-3 p-3 mb-3">
        <div className="row g-3 align-items-end">
          <div className="col-md-2">
            <label className="form-label">Status</label>
            <select
              className="form-select"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="all">All</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">Candidate Name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by name"
              value={nameFilter}
              onChange={(e) => {
                setNameFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-md-2">
            <label className="form-label">Email</label>
            <input
              type="email"
              className="form-control"
              placeholder="Search by email"
              value={emailFilter}
              onChange={(e) => {
                setEmailFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label">NHRC ID</label>
            <input
              type="text"
              className="form-control"
              placeholder="Search by nhrcid"
              value={skillFilter}
              onChange={(e) => {
                setSkillFilter(e.target.value);
                setPage(1);
              }}
            />
          </div>
          <div className="col-md-3 d-flex justify-content-end gap-2">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Loading indicator */}
      {(loading || actionLoading) && (
        <div className="text-center my-3">
          <div className="spinner-border spinner-border-sm text-secondary" role="status" />
          <span className="ms-2 text-muted">
            {actionLoading ? "Processing..." : "Loading..."}
          </span>
        </div>
      )}

      <div className="border-bottom pb-2 mb-2 d-flex align-items-center">
        <div className="form-check me-3">
          <input
            type="checkbox"
            style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
            className="form-check-input border-3"
            checked={allSelected}
            onChange={toggleSelectAll}
          />
        </div>
        <div className="flex-grow-1 d-flex text-muted small fw-semibold">
          <div className="col-2 text-start">Name</div>
          <div className="col-2 text-start">Email</div>
          <div className="col-2 text-start">Phone</div>
          <div className="col-2 text-start">NHRC ID</div>
          <div className="col-2">Status</div>
          <div className="col-2 text-center">Actions</div>
        </div>
      </div>

      {paged.map((c) => (
        <div key={c.id} className="border-bottom py-2 d-flex align-items-center">
          <div className="form-check me-3">
            <input
              type="checkbox"
              style={{ transform: "scale(0.8)", accentColor: "#0d6efd" }}
              className="form-check-input border-3"
              checked={selectedIds.includes(c.id)}
              onChange={() => toggleSelect(c.id)}
            />
          </div>

          <div className="flex-grow-1 d-flex align-items-center">
            <div className="col-2 text-start">
              <div className="fw-semibold">{c.name || c.full_name}</div>
            </div>
            <div className="col-2 text-start">
              <small className="text-break">{c.email}</small>
            </div>
            <div className="col-2 text-start">
              <small>{c.phone}</small>
            </div>
            <div className="col-2 text-start">
              <small>{c.nhrcid || c.nhrc_id}</small>
            </div>
            <div className="col-2">
              <span
                className={`badge ${getStatusBadgeClass(c.status)} d-inline-flex align-items-center justify-content-center`}
                style={{
                  fontSize: "0.85rem",
                  minWidth: "120px",
                  padding: "0.35rem 0.5rem",
                  cursor: "default",
                }}
              >
                {c.status}
              </span>
            </div>

            <div className="col-2 text-center">
              <button
                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                title="View details"
                onClick={() => handleViewCandidate(c.id)}
              >
                <Eye size={16} />
                <span className="d-none d-md-inline">View</span>
              </button>
            </div>
          </div>
        </div>
      ))}

      {!loading && paged.length === 0 && (
        <p className="text-muted mt-3">No candidates match the filters.</p>
      )}

      {/* Pagination */}
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Showing {filteredAll.length === 0 ? 0 : (page - 1) * pageSize + 1}-
          {Math.min(page * pageSize, filteredAll.length)} of {filteredAll.length} responses
        </small>
        <div className="btn-group">
          <button
            className="btn btn-sm border rounded-2 m-2 btn-primary"
            disabled={page === 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <small className="text-muted m-3">
            Page {page} of {Math.max(1, totalPages)}
          </small>
          <button
            className="btn btn-sm border m-2 rounded-2 btn-primary"
            disabled={page === totalPages}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      </div>

      {/* === POPUP START === */}
      <div className={`popup-form-overlay ${showPopup ? "show" : ""}`}>
        <div className="popup-form">

          {/* HEADER */}
          <div className="popup-header">
            <h3>Student Registration</h3>
            <button className="close-btn-res" onClick={() => setShowPopup(false)}>
              &times;
            </button>
          </div>

          {/* ===== REGISTER FORM ===== */}
          <form className="stu-live-pg-register-card" key={selectedCandidate?.id}>

            <div className="stu-live-pg-register-grid">
              <div className="stu-live-pg-register-field">
                <label>Full Name*</label>
                <input
                  readOnly
                  value={selectedCandidate?.name || selectedCandidate?.full_name || ""}
                  required
                />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Mobile Number*</label>
                <input
                  readOnly
                  value={selectedCandidate?.phone || ""}
                  required
                />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Email Id*</label>
                <input
                  type="email"
                  readOnly
                  value={selectedCandidate?.email || ""}
                  required
                />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Location*</label>
                <input
                  readOnly
                  value={selectedCandidate?.location || ""}
                  required
                />
              </div>

              <div className="stu-live-pg-register-field">
                <label>I am a*</label>
                <input
                  readOnly
                  value={selectedCandidate?.member_type || ""}
                  required
                />
              </div>

              <div className="stu-live-pg-register-field">
                <label>NHRC ID*</label>
                <input
                  readOnly
                  value={selectedCandidate?.nhrcid || selectedCandidate?.nhrc_id || ""}
                  required
                />
              </div>

              <div className="stu-live-pg-register-field">
                <label>College Name*</label>
                <input
                  readOnly
                  value={selectedCandidate?.college_name || ""}
                  required
                />
              </div>

              <div className="stu-live-pg-register-field">
                <label>Year of Passout*</label>
                <input
                  readOnly
                  value={selectedCandidate?.year_of_passout || ""}
                  required
                />
              </div>
            </div>

          </form>
        </div>
      </div>
      {/* === POPUP END === */}

    </div>
  );
}

export default GnTotalApplicationsRes;